/* istanbul ignore file */
import { useEffect, useState } from 'react';
import { useOpportunityId } from 'domain/shared/opportunity';
import useOpportunityService, {
  CLOSED_LOST,
  CLOSED_WON,
  CONTRACT,
  OPPORTUNITY_STAGES_ALLOWED_TRANSITIONS,
  SIGNING,
  UPDATE_OPPORTUNITY,
} from 'domain/sales/opportunity/opportunityService';
import { useShowContext } from 'framework/show';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { ButtonWithConfirmation } from 'framework/ui/ui-builder';
import { useForm, DEFAULT_ERROR_MESSAGE, isEmptyFormErrors, FormProvider } from 'framework/form';
import { usePresaleOutputService, presaleOutputValidation } from 'domain/shared/presale';
import ClosedReasonInputs from 'domain/sales/opportunity/shared/OpportunityPipeline/button/MarkStateAsCompletedButton/CloseReasonInputs';
import PresaleOutputInputForm from 'domain/sales/opportunity/shared/OpportunityPipeline/button/MarkStateAsCompletedButton/PresaleOutputInputForm';
import { Box } from '@mui/material';

const MarkStateAsCompletedButton = ({
  activeStep,
  handleStep,
}: {
  activeStep: string;
  handleStep: (step: string) => void;
}) => {
  const id = useOpportunityId();
  const { update } = useOpportunityService() as any;
  const { getLastPresaleOutput, updateOutput } = usePresaleOutputService();
  const { data, setData = () => {} } = useShowContext();
  const { addMessage } = useMessageService();
  const stepAllowedTransitions = OPPORTUNITY_STAGES_ALLOWED_TRANSITIONS[activeStep] || [];
  const nextStep = stepAllowedTransitions[0];
  const [formErrors, setFormErrors] = useState<Dictionary<string[]>>({});
  const [formData, setFormData] = useState<Dictionary<any>>({ stateStep: nextStep });
  const [isConfirmDisabledForOutput, setIsConfirmDisabledForOutput] = useState(false);

  const isClosedReasonInputsVisible = nextStep === CLOSED_WON || nextStep === CLOSED_LOST;

  const isPresaleOutputChangeVisible = nextStep === CONTRACT || nextStep === SIGNING;

  useEffect(() => {
    setFormData({ stateStep: nextStep });
  }, [nextStep]);

  const contextValue = useForm({
    submitFormFunc: () => Promise.resolve({}),
    getValidationSchemaFunc: () => Promise.resolve(presaleOutputValidation),
  });

  useEffect(() => {
    (async () => {
      if (isPresaleOutputChangeVisible) {
        const lastOutput = await getLastPresaleOutput(id);
        const isContractRenewal = data.getValueBySource('isContractRenewal');
        const initialOutputData = lastOutput ? { ...lastOutput, isContractRenewal } : lastOutput;
        contextValue.setFormData(initialOutputData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextStep]);

  useEffect(() => {
    if (isPresaleOutputChangeVisible && contextValue.data?.id) {
      const outputFormData = contextValue.data;
      const outputFormErrors = {
        ...contextValue.validateForm(),
        isValidExpectedProjectStart: !outputFormData.isValidExpectedProjectStart ? ['Please, confirm!'] : [],
        isValidProjectType: !outputFormData.isValidProjectType ? ['Please, confirm!'] : [],
        isValidRequiredResources: !outputFormData.isValidRequiredResources ? ['Please, confirm!'] : [],
      };
      setIsConfirmDisabledForOutput(!isEmptyFormErrors(outputFormErrors));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPresaleOutputChangeVisible, contextValue.data]);

  const handleComplete = async () => {
    if (contextValue.data?.id) {
      const outputFormData = contextValue.data;
      try {
        await updateOutput(id, outputFormData.id, outputFormData);
      } catch (e) {
        addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
        return;
      }
    }
    try {
      const response = await update(id, {
        ...data.getData(),
        ...formData,
        stateStep: nextStep,
      });
      handleStep(nextStep);
      setData(response);
    } catch (e) {
      const result = await (e as any).json();
      addMessage(new ErrorMessage(result?.errors?.stateStep || DEFAULT_ERROR_MESSAGE));
    }
  };

  if (!nextStep) {
    return null;
  }

  const getModalChildren = () => {
    if (isClosedReasonInputsVisible) {
      return (
        <Box mt={2.5}>
          <ClosedReasonInputs
            setFormErrors={setFormErrors}
            setFormData={setFormData}
            formData={formData}
            formErrors={formErrors}
          />
        </Box>
      );
    }
    if (isPresaleOutputChangeVisible && contextValue.data?.id) {
      return (
        <Box mt={2.5}>
          <FormProvider value={contextValue}>
            <PresaleOutputInputForm />
          </FormProvider>
        </Box>
      );
    }
    return <></>;
  };

  const getModalAdditionalMessage = () => {
    if (isClosedReasonInputsVisible) {
      return 'Please, fill out the inputs below.';
    }
    if (isPresaleOutputChangeVisible && contextValue.data?.id) {
      return 'Please, verify that Team structure, Project Type and Expected Project start provided in this form are actual. If not, please, change.';
    }
    return '';
  };

  return (
    <ButtonWithConfirmation
      key={activeStep}
      displayMessage={`Do you really want to mark this state as completed? ${getModalAdditionalMessage()}`}
      submitMethod={handleComplete}
      className="step-button"
      modalChildren={getModalChildren()}
      modalSize={isPresaleOutputChangeVisible && contextValue.data?.id ? 'lg' : 'sm'}
      permissionToCheck={UPDATE_OPPORTUNITY}
      confirmBtnProps={{
        disabled:
          (isClosedReasonInputsVisible &&
            (Object.values(formErrors).some(error => error.length) ||
              !formData.closedReason?.primaryReason ||
              !formData.closedReason?.summary)) ||
          isConfirmDisabledForOutput,
      }}
      closeOnSubmit={false}
    >
      Mark state as completed
    </ButtonWithConfirmation>
  );
};

export default MarkStateAsCompletedButton;
